import type {
  ImportMessageParams,
  MappingWarning,
  RowIssue,
} from "../../types";

/** The `t` from `useTranslation()`, typed loosely: every call here is a plain key plus options. */
export type Translate = (
  key: string,
  options?: Record<string, unknown>
) => string;

/** Whether the current language (or its fallback) has a key — `i18n.exists`. */
export type KeyExists = (key: string) => boolean;

const WARNING_PREFIX = "purchaseRequests.import.warningCodes";
const ISSUE_PREFIX = "purchaseRequests.import.issueCodes";
const FIELD_PREFIX = "purchaseRequests.import.fields";

/**
 * A field the mapping talks about reads better by its label than by `item.quantity`. Only when
 * we have one: an unknown target stays as the backend wrote it, because that is what the buyer
 * has to go and find.
 */
const labelField = (
  params: ImportMessageParams,
  t: Translate,
  exists: KeyExists
): ImportMessageParams => {
  const field = params.field;
  if (typeof field !== "string" || !exists(`${FIELD_PREFIX}.${field}`)) {
    return params;
  }
  return { ...params, field: t(`${FIELD_PREFIX}.${field}`) };
};

/**
 * Our copy for a code when we have it, the backend's English otherwise.
 *
 * Values go through `replace` rather than as top-level options so a param that happens to be
 * called `count` or `lng` fills a placeholder instead of changing how i18next reads the key.
 */
const describe = (
  prefix: string,
  code: string | null | undefined,
  values: ImportMessageParams,
  message: string,
  t: Translate,
  exists: KeyExists
): string => {
  if (!code) return message;
  const key = `${prefix}.${code}`;
  if (!exists(key)) return message;
  return t(key, { replace: labelField(values, t, exists) });
};

/** A mapping warning in the buyer's language. `subject` fills `{{subject}}`. */
export const describeWarning = (
  warning: MappingWarning,
  t: Translate,
  exists: KeyExists
): string =>
  describe(
    WARNING_PREFIX,
    warning.code,
    { ...(warning.params ?? {}), subject: warning.subject },
    warning.message,
    t,
    exists
  );

/** A row issue in the buyer's language. `column` fills `{{column}}`. */
export const describeIssue = (
  issue: RowIssue,
  t: Translate,
  exists: KeyExists
): string =>
  describe(
    ISSUE_PREFIX,
    issue.code,
    { ...(issue.params ?? {}), column: issue.column },
    issue.message,
    t,
    exists
  );
